import { CSSProperties } from 'react';
import styled, { keyframes } from 'styled-components';
import { coreTokens } from '../../core';
import { theme } from './skeleton_loader.theme';

const fadeIn = keyframes`
	0% {
		opacity: 0;
	}
	100% {
		opacity: 1;
	}
`;

const shimmer = keyframes`
	0% {
		background-position: top right;
	}
	100% {
		background-position: top left;
	}
`;

const StyledSkeletonLoader = styled.div<{ $circle: boolean }>`
	position: relative;
	overflow: hidden;
	box-sizing: border-box;
	background: ${theme.getValue('background')};
	border-radius: ${(props) => (props.$circle ? '50%' : theme.getValue('borderRadius'))};

	@media (prefers-reduced-motion: no-preference) {
		animation: ${fadeIn} 150ms;
	}

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(90deg, transparent 33%, ${theme.getValue('shimmerColor')} 50%, transparent 66%) transparent;
		background-repeat: no-repeat;
		background-size: 300% 100%;

		@media (prefers-reduced-motion: no-preference) {
			animation: ${shimmer} 2s infinite;
		}
	}
`;

export interface SkeletonLoaderProps {
	/**
	 * CSS width, e.g. `'100%'`, `'120px'`.
	 * @default '200px'
	 */
	width?: string;

	/**
	 * CSS height, e.g. `'20px'`.
	 * @default coreTokens.size.x2_5
	 */
	height?: string;

	/**
	 * Renders a circular shape (e.g. for avatar placeholders) instead of a
	 * rounded bar.
	 * @default false
	 */
	circle?: boolean;
}

/**
 * A shimmering placeholder bar (or circle) used while content is loading. This is a purely visual component. For accessibility purposes, you need to expose the loading state with an `aria-busy` attribute and a nearby text element indicating that content is loading.
 */
export function SkeletonLoader({ width = '200px', height = coreTokens.size.x2_5, circle = false }: SkeletonLoaderProps): JSX.Element {
	const style: CSSProperties = { width, height };

	return <StyledSkeletonLoader $circle={circle} style={style} />;
}
